import React, {Fragment, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {Button, Intent, NonIdealState, FormGroup} from '@blueprintjs/core';
import {useHistory, useParams, useLocation} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {Breadcrumbs, Loading} from 'worksource-ui';
import mixpanel from 'mixpanel-browser';

import AnalyticsActions from 'services/analytics/actions';
import {EventIdentifier} from 'services/analytics/constants';
import {showGQLError, showSuccess} from 'utils/toaster';
import {useUIActions} from 'utils/ui-context';
import HeaderBar from 'components/header-bar';

const BLOCKED_DOMAINS = ['vimeo.com'];

const FILE_FRAGMENT = gql`
  fragment FileViewerFile on File {
    id,
    name,
    url,
    downloadUrl,
    notes,
    createdAt,
    updatedAt,
    favorite,
    confidential,
  }
`;
const FETCH_FILE = gql`
  query FileViewerDialogFetch($clientId: ID!, $guideId: ID!, $fileId: ID!, $parentPageId: ID!, $skipParent: Boolean!) {
    guides(clientId: $clientId, id: $guideId) {
      name,
      file(id: $fileId) {
        ...FileViewerFile,
      },
      parentPage: page(id: $parentPageId) @skip(if: $skipParent) {
        id,
        name,
      }
    },
  }
  ${FILE_FRAGMENT}
`;

const SET_FAVORITE = gql`
  mutation FileViewerDialogSetFavorite($fileId: ID!, $value: Boolean!) {
    setFavorite(fileId: $fileId, value: $value) {
      file {
        ...FileViewerFile,
      },
      errors {
        title,
        message
      }
    }
  }
  ${FILE_FRAGMENT}
`;

const FileViewerDialog = () => {
  ////
  // Hooks
  //
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {clientId, guideId, pageId, fileId} = useParams();
  const {clearDialog} = useUIActions();

  useEffect(() => {
    mixpanel.track('Open file viewer', {clientId, guideId, pageId, fileId});
    dispatch(AnalyticsActions.trigger({
      clientId,
      guideId,
      identifier: EventIdentifier.FILE_OPEN,
      additionals: {file_id: fileId},
    }));
  }, []);

  const searchQuery = new URLSearchParams(location.search);
  // True if the URL tells us we're currently displaying the page from the
  // favorites viewer page
  const fromFavorites = !!searchQuery.get('favorites');

  ////
  // GraphQL
  //
  const {loading: loadingFetch, data} = useQuery(FETCH_FILE, {
    variables: {clientId, guideId, fileId, parentPageId: (pageId || ''), skipParent: !pageId},
    onError: error => showGQLError({error}),
  });
  const [setFavorite, {loading: loadingSetFavorite}] = useMutation(SET_FAVORITE, {
    onCompleted: ({setFavorite: result}) => showSuccess({
      message: result.file.favorite ? 'Favorite saved' : 'Favorite removed',
    }),
    onError: error => showGQLError({error}),
    refetchQueries: ['FileViewerDialogFetch', 'GuidesFavoritesPageFetch'],
  });

  ////
  // Actions
  //
  const close = () => {
    if (pageId) {
      history.push(`/clients/${clientId}/guides/${guideId}/pages/${pageId}`);
    } else if (fromFavorites) {
      history.push(`/clients/${clientId}/guides/${guideId}/favorites`);
    } else {
      history.push(`/clients/${clientId}/guides/${guideId}`);
    }
  };

  ////
  // Render
  //
  if (loadingFetch || !data) {
    return (
      <div className="file-viewer--container">
        <HeaderBar>
          <Breadcrumbs>
            <Breadcrumbs.Breadcrumb
              text="Some Guide"
              href="#"
              className="bp3-skeleton"
            />
            <Breadcrumbs.Breadcrumb
              text="Some Page"
              className="bp3-skeleton"
              href="#"
            />
            <Breadcrumbs.Breadcrumb
              text="Some file"
              className="bp3-skeleton"
              href="#"
            />
          </Breadcrumbs>
          <div className="spacer" />
          <Button
            intent={Intent.PRIMARY}
            icon="cross"
            text="Close"
            onClick={close}
          />
        </HeaderBar>
        <div className="file-viewer--content">
          <Loading />
        </div>
        <div className="file-viewer--sidebar">
          <h1 className="bp3-skeleton">Skeleton</h1>
          <p className="bp3-skeleton">Some content goes here. Lorem ipsum sit amet.</p>
          <p className="bp3-skeleton">Some content goes here. Lorem ipsum sit amet.</p>
        </div>
      </div>
    );
  }

  const {guides: [guide]} = data;
  const {file, parentPage} = guide;

  // If we're using a plain vimeo link then let's see if we can embed it
  // directly by extracting out the ID
  const vimeoMatch = file.url.match(/^https:\/\/vimeo.com\/(\d*)$/);
  if (vimeoMatch) {
    const [, videoId] = vimeoMatch;
    file.url = `https://player.vimeo.com/video/${videoId}`;
  }

  // Determine if this file is on a blocked domain or not (ie they block
  // iframes)
  const fileUrl = new URL(file.url);
  const onBlockedDomain = BLOCKED_DOMAINS.includes(fileUrl.host) && !vimeoMatch;

  return (
    <div className="file-viewer--container">
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            text={guide.name}
            onClick={() => {
              clearDialog();
              history.push(`/clients/${clientId}/guides/${guideId}`);
            }}
          />
          {parentPage && (
            <Breadcrumbs.Breadcrumb
              text={parentPage.name}
              href={`/clients/${clientId}/guides/${guideId}/pages/${parentPage.id}`}
            />
          )}
          {fromFavorites && (
            <Breadcrumbs.Breadcrumb
              text="Favorites"
              href={`/clients/${clientId}/guides/${guideId}/favorites`}
            />
          )}
          <Breadcrumbs.Breadcrumb
            current
            text={file.name}
            href="#"
          />
        </Breadcrumbs>
        <div className="spacer" />
        <Button
          intent={Intent.PRIMARY}
          icon="cross"
          text="Close"
          onClick={close}
        />
      </HeaderBar>
      {!onBlockedDomain && (
        <div className="file-viewer--content">
          <iframe src={file.url} title="File viewer" />
        </div>
      )}
      {onBlockedDomain && (
        <div className="file-viewer--content">
          <NonIdealState
            icon="link"
            title="Inline viewer not available"
            description={(
              `The host of this content (${fileUrl.host}) does not allow us to`
              + ' show their content in our file viewer. Click the button'
              + ' below to open this page in a new tab.'
            )}
            action={(
              <Button
                text="Open file in new tab"
                icon="share"
                intent={Intent.PRIMARY}
                onClick={() => window.open(file.url, '_blank')}
              />
            )}
          />
        </div>
      )}
      <div className="file-viewer--sidebar">
        <h3>Actions</h3>
        <Button
          minimal
          large
          fill
          text={file.favorite ? 'Remove from favorites' : 'Add to favorites'}
          icon={file.favorite ? 'star' : 'star-empty'}
          loading={loadingSetFavorite}
          disabled={loadingSetFavorite}
          onClick={() => setFavorite({variables: {
            fileId: file.id,
            value: !file.favorite,
          }})}
        />
        {file.downloadUrl && (
          <Button
            minimal
            large
            fill
            disabled={file.confidential}
            text="Download"
            icon="download"
            onClick={() => window.open(file.downloadUrl)}
          />
        )}
        <FormGroup
          helperText={(file.confidential
            ? 'This file has been marked as confidential by your administrator. It cannot be shared or downloaded.'
            : ''
          )}
        >
          <Button
            minimal
            large
            fill
            disabled={file.confidential}
            text="Share"
            icon="share"
          />
        </FormGroup>

        <h3>Last updated</h3>
        {moment(file.updatedAt || file.createdAt).fromNow()}

        {file.notes && (
          <Fragment>
            <h3>Notes</h3>
            <p>{file.notes}</p>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default FileViewerDialog;
